/**
 * @graphql agg
 */

import { useCallback } from 'react';
import { graphql } from 'react-relay';
import { type FetchResult } from 'apollo-link';

import { useWriteFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useWriteFragmentInterop.ts';
import { useReadFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useReadFragmentInterop.ts';

import type { useUpdateReactionInCacheFragment$data } from './__generated__/useUpdateReactionInCacheFragment.graphql.ts';

// Aliased types (for easier reading/passing around):
export type CachedReactionsSummaryFragment = useUpdateReactionInCacheFragment$data;
export type ReactionSummary = CachedReactionsSummaryFragment['reactionsSummaryForEmoji'][number];

// reactionsSummary is not supported in the normal Comment fragment cache update functions because
// GQL fragments do not support nested arguments in fragments. However, while the reactionsSummary
// objects do not have an explicit ID field, they are uniquely identified in the cache by the
// combination of its arguments. So in this file we create a separate fragment and associated
// function to update just comment reactions in cache (where the arguments effectively become a
// first-level arguments to the fragment and not a nested ones). And this is okay, because the
// reactionsSummary is (so far) always updated separately from the rest of the comment data.

const updateReactionInCacheFragment = graphql`
	fragment useUpdateReactionInCacheFragment on ReactionsSummaryResponse {
		ari
		containerAri
		reactionsCount
		reactionsSummaryForEmoji {
			id
			emojiId
			count
			reacted
			__typename
		}
	}
`;

export type UpdateReactionsCallback<TData> = (
	reactionsSummary: CachedReactionsSummaryFragment,
	mutationResult: FetchResult<TData>,
) => CachedReactionsSummaryFragment;

type UpdateReactionsInCacheProps<TData> = {
	mutationResult: FetchResult<TData>;
	commentId: string;
	pageId: string;
	contentType: string;
	emojiId: string;
	actionType: 'add' | 'delete';
	onError?: (error: Error) => void;
};

// Create type aliases for results of confluence_addReaction and deleteReaction mutations rather
// than use the generated ones so we aren't dependent on specific type files - even though this is
// copied from the Apollo-generated type files.  This avoids a dependency loop between packages.
// Note the "| undefined" on some variables is needed to be compatible with Relay-generated type
// files.
type ConfluenceReactionPayload = {
	__typename?: 'ConfluenceReactionPayload' | 'SaveReactionResponse';
	ari: string | null;
	containerAri: string | null;
	emojiId: string | null;
};

type AddReactionMutationData = {
	__typename?: 'Mutation';
	confluence_addReaction: ConfluenceReactionPayload | null;
};

type DeleteReactionMutationData = {
	__typename?: 'Mutation';
	deleteReaction: ConfluenceReactionPayload | null;
};

type ReactionActionProps = {
	reactionMutationPayload: ConfluenceReactionPayload;
	cachedReactionsSummary: CachedReactionsSummaryFragment;
	emojiId: string;
};

/**
 * Adds a new reaction to the cached reactions summary.
 * @param props
 * @param props.reactionMutationPayload - The payload result of the add reaction mutation.
 * @param props.cachedReactionsSummary - The current cached reactions summary.
 * @param props.emojiId - The ID of the emoji being added.
 * @returns
 */
const addReaction = ({
	reactionMutationPayload,
	cachedReactionsSummary,
	emojiId,
}: ReactionActionProps): useUpdateReactionInCacheFragment$data => {
	const { reactionsCount, reactionsSummaryForEmoji } = cachedReactionsSummary || {};

	const newReactionsAri = reactionMutationPayload.ari;
	const newReactionsContainerAri = reactionMutationPayload.containerAri;
	// (Use passed-in emojiId rather than mutationResult data as it's possible that it's not in the result)

	// Manually create a deep copy of the cached reactions, as the Relay-style readFragment types
	// are read-only objects and properties cannot be modified directly.  updatedReactions is a mutable
	// array of readonly reaction objects.
	const updatedReactions = [...(reactionsSummaryForEmoji || [])];
	let updatedReactionsCount = reactionsCount || 0;

	// Try to find the emojiId in the existing reactionsSummary
	const foundReactionIdx = updatedReactions.findIndex(
		(reaction) => reaction && reaction?.emojiId === emojiId,
	);
	// If the reaction already exists, update its attributes.  If not, create a new one.
	const foundReaction = foundReactionIdx !== -1 ? updatedReactions[foundReactionIdx] : undefined;
	if (foundReaction && !foundReaction.reacted) {
		// Create an immutable new updated reaction object so typescript is happy
		const updatedReaction = {
			...foundReaction,
			count: foundReaction.count ? foundReaction.count + 1 : 1,
			reacted: true,
		} as ReactionSummary;
		updatedReactions[foundReactionIdx] = updatedReaction;
		updatedReactionsCount += 1;
	} else if (!foundReaction) {
		// Otherwise, create a new reaction
		const newReaction: ReactionSummary = {
			emojiId,
			count: 1,
			reacted: true,
			id: `${newReactionsContainerAri}|${newReactionsAri}|${emojiId}`,
			__typename: 'ReactionsSummaryForEmoji',
		};

		updatedReactions.push(newReaction);
		updatedReactionsCount += 1;
	}

	return {
		...cachedReactionsSummary,
		reactionsSummaryForEmoji: updatedReactions,
		reactionsCount: updatedReactionsCount,
	};
};

/**
 * deletes an existing reaction to the cached reactions summary.
 * @param props
 * @param props.reactionMutationPayload - The payload result of the add reaction mutation.
 * @param props.cachedReactionsSummary - The current cached reactions summary.
 * @param props.emojiId - The ID of the emoji being removed.
 * @returns
 */
const deleteReaction = ({
	cachedReactionsSummary,
	emojiId,
}: ReactionActionProps): useUpdateReactionInCacheFragment$data => {
	const { reactionsCount, reactionsSummaryForEmoji } = cachedReactionsSummary || {};

	// (Use passed-in emojiId rather than mutationResult data as it's posible that it's not in the result)

	// Manually create a deep copy of the cached creactions, as the Relay-style readFragment types
	// are read-only objects and properties cannot be modified directly. updatedReactions is an mutable
	// array of readonly reaction objects.
	const updatedReactions = [...(reactionsSummaryForEmoji || [])];
	let updatedReactionsCount = reactionsCount || 1;

	// Try to find the emojiId in the existing reactionsSummary
	const foundReactionIdx = updatedReactions.findIndex(
		(reaction) => reaction && reaction?.emojiId === emojiId,
	);

	// If reaction exists, update its attributes
	const foundReaction = foundReactionIdx !== -1 ? updatedReactions[foundReactionIdx] : undefined;
	if (foundReaction) {
		if (foundReaction.count > 1 && foundReaction.reacted) {
			// Create an immutable new updated reaction object so typescript is happy
			const updatedReaction = {
				...foundReaction,
				count: (updatedReactions[foundReactionIdx]?.count ?? 0) - 1,
				reacted: false,
			} as ReactionSummary;
			updatedReactions[foundReactionIdx] = updatedReaction;
			updatedReactionsCount -= 1;
		} else if (foundReaction.count == 1 && foundReaction.reacted) {
			// Otherwise, remove the reaction entirely
			updatedReactions.splice(foundReactionIdx, 1);
			updatedReactionsCount -= 1;
		}
	}

	return {
		...cachedReactionsSummary,
		reactionsSummaryForEmoji: updatedReactions,
		reactionsCount: updatedReactionsCount,
	};
};

/**
 * This hook returns a function that can be used to perform updates to a single comment's
 * reactionsSummary in the Apollo GraphQL cache after a mutation that modifies the reactionsSummary
 * has successfully completed. These mutations either add a new reaction or remove an existing
 * reaction.
 *
 * @returns function to update a comment's reactionsSummary in cache
 */
export const useUpdateReactionInCache = () => {
	const readFragment = useReadFragmentInterop<useUpdateReactionInCacheFragment$data>({
		fragmentSpec: updateReactionInCacheFragment,
	});

	const writeFragment = useWriteFragmentInterop<
		Omit<useUpdateReactionInCacheFragment$data, ' $fragmentType'>
	>({
		fragmentSpec: updateReactionInCacheFragment,
	});

	/**
	 * Function to update a comment in the Apollo cache.
	 * @param mutationResult - The result of the mutation that triggered the cache update
	 * @param commentId - ID of the comment to update
	 * @param	pageId - ID of the page containing the comment
	 * @param contentType - The content type ('page' or 'blogpost', etc.)
	 * @param	emojiId - The emoji for the reaction being added or deleted
	 * @param actionType - The type of action being performed ('add' or 'delete')
	 * @param onError - Optional error callback to handle errors during the update process
	 */
	const updateReactionInCache = useCallback(
		({
			mutationResult,
			commentId,
			pageId,
			contentType,
			actionType,
			emojiId,
			onError,
		}: UpdateReactionsInCacheProps<AddReactionMutationData | DeleteReactionMutationData>) => {
			if (!commentId) {
				onError?.(new Error('Cannot update comment reactions in cache: comment ID is missing'));
				return;
			}
			if (!pageId) {
				onError?.(new Error('Cannot update comment reactions in cache: page ID is missing'));
				return;
			}

			try {
				// The fragment ID format for Apollo cache
				const fragmentId = `$Comment:${commentId}.reactionsSummary({"childType":"comment","contentType":"${contentType}","pageId":"${pageId}"})`;

				// Read the comment's reaction from cache
				const cachedReactionsSummary = readFragment({ id: fragmentId });

				if (!cachedReactionsSummary) {
					// (There is a slight chance the reactionsSummary is not in cache - but the mutation that
					// calls this function would have to completed successfully to call us, so presumably the
					// reactionsSummary existed then. Even if another user deleted the owning comment, its item
					// in the cache remains. So not being in the cache is unlikely, so let's log this as an
					// error and return. If in the future we find the legit use case is happening more often, we
					// can change to a warning instead.)
					onError?.(
						new Error(
							`Cannot update comment reactions in cache: comment reactions with ID ${commentId} and page ID ${pageId} not found in cache`,
						),
					);
					return;
				}

				const payload = mutationResult.data ?? undefined;
				let reactionMutationPayload: ConfluenceReactionPayload | undefined;
				if (payload) {
					if ('confluence_addReaction' in payload) {
						reactionMutationPayload = payload.confluence_addReaction || undefined;
					} else if ('deleteReaction' in payload) {
						reactionMutationPayload = payload.deleteReaction || undefined;
					}
				}

				if (!reactionMutationPayload) {
					onError?.(
						new Error('Cannot update comment reactions in cache: mutation result data is missing'),
					);
					return;
				}

				let updatedReactionsSummary: CachedReactionsSummaryFragment;
				const reactionActionProps = {
					reactionMutationPayload,
					cachedReactionsSummary,
					emojiId,
				};

				switch (actionType) {
					case 'add': {
						updatedReactionsSummary = addReaction(reactionActionProps);
						break;
					}
					case 'delete': {
						updatedReactionsSummary = deleteReaction(reactionActionProps);
						break;
					}
					default: {
						onError?.(
							new Error(`Unknown comment reactions cache operation specified: ${actionType}`),
						);
						return;
					}
				}

				// Write the updated comment back to cache
				writeFragment({
					fragmentId,
					data: updatedReactionsSummary,
				});
			} catch (err) {
				onError?.(
					new Error(`An error occurred when updating cached comment reactions data: ${err}`),
				);
				return;
			}
		},
		[readFragment, writeFragment],
	);

	return { updateReactionInCache };
};
